import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateDeviceRequestSchema, type UpdateDeviceRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useUpdateDevice, type DeviceSummary } from "@/features/devices/hooks";

interface ClpEditFormProps {
  device: DeviceSummary;
  onDirtyChange: (dirty: boolean) => void;
}

export function ClpEditForm({ device, onDirtyChange }: ClpEditFormProps) {
  const update = useUpdateDevice(device.id);
  const form = useForm<UpdateDeviceRequest>({
    resolver: zodResolver(UpdateDeviceRequestSchema),
    defaultValues: { code: device.code, name: device.name },
  });
  const dirty = form.formState.isDirty;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  async function onSubmit(values: UpdateDeviceRequest) {
    try {
      await update.mutateAsync(values);
      toast.success("CLP atualizado.");
      form.reset(values);
    } catch (err) {
      toast.error("Falha ao salvar.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const errs = form.formState.errors;
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="clp-code">Código</Label>
          <Input id="clp-code" {...form.register("code")} />
          {errs.code && <p className="text-xs text-destructive">{errs.code.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="clp-name">Nome</Label>
          <Input id="clp-name" {...form.register("name")} />
          {errs.name && <p className="text-xs text-destructive">{errs.name.message}</p>}
        </div>
      </div>
      <Button type="submit" size="sm" disabled={!dirty || update.isPending}>
        {update.isPending ? "Salvando…" : "Salvar"}
      </Button>
    </form>
  );
}
